import { handoffStalledFailure } from "./loading-failure.ts";
import type { Welcome } from "./connection.ts";
import type { HandoffFrame } from "./handoff-wire.ts";
import { worldLoadFailure } from "./older-build.ts";
import { gameReport } from "./report.ts";
import { PAGE_MOTION } from "./page-motion.ts";
import type { SessionLifetime, SessionParts } from "./session-types.ts";

/**
 * The length of the fade to black and of the fade back (ADR-122 decision 2.9, the designer's proposal), in milliseconds.
 */
export const HANDOFF_FADE_MS = 200;

/**
 * How long the screen stays fully black before the join's loading screen shows over it (ADR-122 decision 2.9, the
 * author's ruling of 2026-10-29); a handoff that lands sooner shows no loading screen at all.
 */
export const HANDOFF_SHELL_MS = 1000;

/**
 * How long a handoff may run, from `handoffInitiated`, before the page gives it up with a notice and a Retry that
 * rejoins by the normal path (the author's ruling of 2026-10-29).
 */
export const HANDOFF_STALL_MS = 15_000;

/**
 * A character's move to another map on the same socket (ADR-106 decision 19, ADR-122 decision 2.9): the page fades to
 * black, drops the world it holds, loads the target map's chunks, and only then reads the frames the connection held
 * since `handoffInitiated`, the new `welcome` first, so nothing of the map left is drawn after the black and nothing of
 * the map reached is read before its stage stands. It fades in once the new map is drawn with the character on it. The
 * page never predicts a door.
 *
 * The same machinery stands the page on the map a `welcome` names when that is not the map it holds, as a join that
 * saved the character inside a room does (`standing`). A death return to a shrine on another map is this move with a
 * different cause; its words are P2.11-T27's.
 */
export class SessionHandoff {
  private shellTimer: ReturnType<typeof setTimeout> | null = null;
  private stallTimer: ReturnType<typeof setTimeout> | null = null;
  private generation = 0;

  /**
   * @param parts The session's parts.
   * @param lifetime The session's own moves.
   */
  public constructor(
    private readonly parts: SessionParts,
    private readonly lifetime: SessionLifetime,
  ) {}

  /**
   * Whether a handoff is in progress, from `handoffInitiated` until the new map is drawn.
   *
   * @returns True while one is.
   */
  public get active(): boolean {
    return this.parts.state.handoff !== null;
  }

  /**
   * Whether the page waits for the new map's welcome with the world dropped: nothing moves and nothing is sent meanwhile.
   *
   * @returns True from `handoffInitiated` until the new map's welcome is applied.
   */
  public get waiting(): boolean {
    const handoff = this.parts.state.handoff;
    return handoff !== null && !handoff.landed && !handoff.seamless;
  }

  /**
   * Whether a seamless crossing waits for its welcome (ADR-147 decision 18): the world is still shown and played.
   *
   * @returns True from a crossing's `handoffInitiated` until its welcome is taken.
   */
  public get crossing(): boolean {
    return this.parts.state.handoff?.seamless === true;
  }

  /**
   * Begins a handoff on `handoffInitiated`: the connection is already holding every later frame. The whole page fades
   * out, the world is dropped, the target map's stage is built, and the held frames are read.
   *
   * @param frame The frame.
   */
  public begin(frame: HandoffFrame): void {
    const { state, host } = this.parts;
    const connection = this.parts.connection();
    if (host.maps === undefined || connection === null) {
      connection?.resume();
      return;
    }
    this.stopTimers();
    this.generation += 1;
    const generation = this.generation;
    const maps = host.maps;
    const seamless = frame.cause === "travel" && maps.holds?.(frame.map) === true && maps.boot !== undefined && maps.cross !== undefined;
    state.handoff = { map: frame.map, name: frame.name, cause: frame.cause, shell: false, landed: false, seamless };
    this.stallTimer = setTimeout(() => this.stalled(generation), HANDOFF_STALL_MS);
    const version = host.world()?.version ?? null;
    void (seamless ? this.cross(frame.map, version, generation) : this.carry(frame.map, version, generation));
  }

  /**
   * Runs a seamless crossing's steps (ADR-147 decision 18): the map reached is booted behind the world shown, with no
   * fade and no loading screen, while the character keeps walking on the neighbour's ground the page holds; then the
   * frames held since `handoffInitiated` are read with the view kept, so the map left stays drawn until the welcome moves
   * the frame. A boot that fails falls back to the door's way: the fade, the world dropped, the map loaded.
   *
   * @param map The map reached.
   * @param version The world version the page held, for a load failure that names it.
   * @param generation The handoff's number.
   */
  private async cross(map: string, version: string | null, generation: number): Promise<void> {
    try {
      await this.parts.host.maps?.boot?.(map);
    } catch (error) {
      if (generation === this.generation && this.parts.state.handoff !== null) {
        console.warn(`the crossing to ${map} shows the loading screen: ${error instanceof Error ? error.message : String(error)}`);
        this.parts.state.handoff.seamless = false;
        void this.carry(map, version, generation);
      }
      return;
    }
    if (generation === this.generation) {
      this.parts.connection()?.resume(true);
    }
  }

  /**
   * Turns a seamless crossing whose welcome cannot move the frame into a door's handoff (ADR-147 note 14): the booted
   * neighbour is forgotten and a boot still running outdated, so nothing is booted twice or kept, and the page goes black
   * at once, so the world dropped as the welcome stands the page on its map is never seen; the arrival fades back in as a
   * door's does.
   */
  public fallBack(): void {
    const handoff = this.parts.state.handoff;
    if (handoff === null || !handoff.seamless) {
      return;
    }
    handoff.seamless = false;
    this.parts.host.maps?.drop?.();
    void this.parts.host.maps?.fade("out", 1);
  }

  /**
   * Ends a seamless crossing once its welcome has moved the frame: nothing faded, so nothing fades back.
   */
  public crossed(): void {
    if (this.parts.state.handoff === null) {
      return;
    }
    this.generation += 1;
    this.stopTimers();
    this.parts.state.handoff = null;
  }

  /**
   * Runs a handoff's steps in order: the fade out, the drop of the world, the new stage, and the held frames.
   *
   * @param map The target map.
   * @param version The world version the page held, for a load failure that names it.
   * @param generation The handoff's number, which a newer handoff or a cancel outdates.
   */
  private async carry(map: string, version: string | null, generation: number): Promise<void> {
    const maps = this.parts.host.maps;
    try {
      await maps?.fade("out", fadeMs());
      if (generation !== this.generation) {
        return;
      }
      this.shellTimer = setTimeout(() => this.showShell(generation), HANDOFF_SHELL_MS);
      this.lifetime.reset();
      await maps?.enter(map);
      const delay = this.parts.options.handoffDelayMs ?? 0;
      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      if (generation === this.generation) {
        this.parts.connection()?.resume();
      }
    } catch (error) {
      if (generation === this.generation) {
        this.failed(error, version);
      }
    }
  }

  /**
   * Stands the page on the map a welcome names. A welcome for the map the page holds is read as it is. For another map
   * the connection holds the frames after it, the stage for that map is built, and the welcome is read again.
   *
   * @param welcome The welcome.
   * @param read Reads the welcome.
   * @returns True when the welcome may be read now; false when it will be read again once the map stands.
   */
  public standing(welcome: Welcome, read: (welcome: Welcome) => void): boolean {
    const maps = this.parts.host.maps;
    const connection = this.parts.connection();
    if (maps === undefined || connection === null) {
      return true;
    }
    const current = maps.current();
    if (current === null || current === welcome.map) {
      return true;
    }
    const version = this.parts.host.world()?.version ?? null;
    connection.hold();
    const stall = setTimeout(() => {
      if (this.parts.connection() === connection) {
        gameReport.closed = `no map within ${HANDOFF_STALL_MS / 1000} s of the welcome`;
        this.lifetime.stalled(handoffStalledFailure());
      }
    }, HANDOFF_STALL_MS);
    void maps.enter(welcome.map).then(
      () => {
        clearTimeout(stall);
        if (this.parts.connection() === connection) {
          read(welcome);
          connection.resume();
        }
      },
      (error: unknown) => {
        clearTimeout(stall);
        this.failed(error, version);
      },
    );
    return false;
  }

  /**
   * Ends a handoff once the new map is drawn with the character on it: the world fades in, and the loading screen, when
   * it showed, gives way.
   */
  public arrived(): void {
    const handoff = this.parts.state.handoff;
    if (handoff === null) {
      return;
    }
    this.generation += 1;
    this.stopTimers();
    this.parts.state.handoff = null;
    if (handoff.shell) {
      this.parts.options.progress({ type: "revealed" });
    }
    void this.parts.host.maps?.fade("in", fadeMs());
  }

  /**
   * Ends a handoff that cannot finish, because the connection ended: the world fades back in under whatever the
   * session shows next.
   */
  public cancel(): void {
    if (this.parts.state.handoff === null) {
      return;
    }
    this.generation += 1;
    this.stopTimers();
    this.parts.state.handoff = null;
    void this.parts.host.maps?.fade("in", 1);
  }

  /**
   * Gives up a handoff that has run past `HANDOFF_STALL_MS`: the connection is dropped and the loading shell shows the
   * notice with Retry, which joins again by the normal path and lands where the server holds the character.
   *
   * @param generation The handoff's number.
   */
  private stalled(generation: number): void {
    if (generation !== this.generation || this.parts.state.handoff === null) {
      return;
    }
    gameReport.closed = `no map within ${HANDOFF_STALL_MS / 1000} s of the handoff`;
    this.lifetime.stalled(handoffStalledFailure());
  }

  /**
   * Shows the join's loading screen over the held black, once a handoff has run past `HANDOFF_SHELL_MS`.
   *
   * @param generation The handoff's number.
   */
  private showShell(generation: number): void {
    const handoff = this.parts.state.handoff;
    if (generation !== this.generation || handoff === null) {
      return;
    }
    handoff.shell = true;
    this.parts.options.progress({ type: "departed" });
  }

  /**
   * A handoff's map failed to load: the page gives the connection up with the world's failure, or reloads for a world
   * written for a newer build.
   *
   * @param error What the load threw.
   * @param version The world version the page held.
   */
  private failed(error: unknown, version: string | null): void {
    this.cancel();
    const failure = worldLoadFailure(error, version);
    if (failure !== null) {
      this.lifetime.giveUp(failure);
    }
  }

  /**
   * Stops both timers of a handoff: the one that shows the loading screen and the one that gives the handoff up.
   */
  private stopTimers(): void {
    if (this.shellTimer !== null) {
      clearTimeout(this.shellTimer);
      this.shellTimer = null;
    }
    if (this.stallTimer !== null) {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
  }
}

/**
 * The fade's length now: none for a player who asked for reduced motion.
 *
 * @returns The length in milliseconds.
 */
function fadeMs(): number {
  return PAGE_MOTION.reduced() ? 1 : HANDOFF_FADE_MS;
}
